import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { cn } from "@-/com.npmjs.cn";
import type { HanziScriptToggleGate } from "#use-hanzi-script-toggle-gate";

const scriptGlyphByValue = {
  simplified: "简",
  traditional: "繁",
} satisfies Record<HanziScript, string>;

/**
 * Requires a {@link HanziScriptToggleGate} from `useHanziScriptToggleGate()`: the toggle exists only
 * while Traditional characters are offered, and the parent decides what shows otherwise. The gate
 * is proof of visibility only; it carries no render data.
 */
export function HanziScriptToggle({
  compact = false,
  onChange,
  value,
}: {
  compact?: boolean;
  gate: HanziScriptToggleGate;
  onChange: (value: HanziScript) => void;
  value: HanziScript;
}) {
  const scriptToggle = (
    <View className="flex-row items-center rounded-full bg-muted/40 p-0.5">
      {(["simplified", "traditional"] as const).map((scriptValue) => {
        const isSelected = value === scriptValue;
        return (
          <Pressable
            accessibilityLabel={
              scriptValue === "simplified"
                ? "Simplified Chinese script"
                : "Traditional Chinese script"
            }
            accessibilityState={{ selected: isSelected }}
            className={cn(
              "flex-row items-center justify-center rounded-full px-2",
              compact ? "h-6 min-w-8" : "h-8 min-w-10",
              isSelected && "bg-background shadow-sm",
            )}
            key={scriptValue}
            onPress={() => {
              onChange(scriptValue);
            }}
            testID={`hanzi-script-${scriptValue}`}
          >
            <Text
              className={cn(
                compact ? "text-sm leading-5" : "text-base leading-6",
                isSelected ? "text-foreground" : "text-muted-foreground/60",
              )}
            >
              {scriptGlyphByValue[scriptValue]}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );

  if (compact) {
    return <View className="items-center">{scriptToggle}</View>;
  }

  return (
    <View className="flex-row items-center justify-center gap-3 p-4">
      <Text
        className={cn(
          "text-sm",
          value === "simplified" ? "font-medium text-foreground" : "text-muted-foreground",
        )}
      >
        Simplified
      </Text>
      {scriptToggle}
      <Text
        className={cn(
          "text-sm",
          value === "traditional" ? "font-medium text-foreground" : "text-muted-foreground",
        )}
      >
        Traditional
      </Text>
    </View>
  );
}
